import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, User, Loader, AlertTriangle, X, Upload, Download, FileText, CheckCircle, XCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import ApiService from './ApiService';
import { usePermissions, PERMISSIONS } from './usePermissions';
import AccessDenied from './AccessDenied';
import PatientStatsWidget from './PatientStatsWidget';

// Patient Modal Component (same as before, just extracted for clarity)
const PatientModal = ({ isOpen, onClose, onSubmit, title, submitText, formData, setFormData, error, saving, t }) => {
  if (!isOpen) return null;

  const handleNameChange = (field, value) => {
    const lettersOnly = value.replace(/[^a-zA-Z\s-]/g, '');
    setFormData({ ...formData, [field]: lettersOnly });
  };

  const handlePhoneChange = (value) => {
    const numbers = value.replace(/\D/g, '');
    const limited = numbers.slice(0, 10);
    let formatted = limited;
    if (limited.length >= 7) {
      formatted = `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
    } else if (limited.length >= 4) {
      formatted = `${limited.slice(0, 3)}-${limited.slice(3)}`;
    }
    setFormData({ ...formData, phone: formatted });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleNameChange('firstName', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleNameChange('lastName', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                placeholder="123-45-6789"
                maxLength="12"
              />
              <p className="text-xs text-gray-500 mt-1">Format: XXX-XX-XXXX</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Cellphone Provider
              </label>
              <select
                value={formData.cellphoneProvider}
                onChange={(e) => setFormData({ ...formData, cellphoneProvider: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
              >
                <option value="">Select Provider</option>
                <option value="Verizon">Verizon</option>
                <option value="AT&T">AT&T</option>
                <option value="T-Mobile">T-Mobile</option>
                <option value="Mint">Mint</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Timezone
              </label>
              <select
                value={formData.timezone}
                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
              >
                <option value="">Clinic default</option>
                <option value="America/New_York">Eastern (New York)</option>
                <option value="America/Chicago">Central (Chicago)</option>
                <option value="America/Denver">Mountain (Denver)</option>
                <option value="America/Phoenix">Arizona (Phoenix)</option>
                <option value="America/Los_Angeles">Pacific (Los Angeles)</option>
                <option value="America/Anchorage">Alaska</option>
                <option value="Pacific/Honolulu">Hawaii</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Reminders send on the patient's local time</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Date of Birth
              </label>
              <input
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
              />
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">
                Reminder Preferences
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="preferSMS"
                  checked={formData.preferSMS}
                  onChange={(e) => setFormData({ ...formData, preferSMS: e.target.checked })}
                  className="w-4 h-4 text-spruce-600 rounded focus:ring-spruce-500"
                />
                <label htmlFor="preferSMS" className="ml-2 text-sm text-gray-700">
                  Prefer SMS Text Message Reminders
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="preferEmail"
                  checked={formData.preferEmail}
                  onChange={(e) => setFormData({ ...formData, preferEmail: e.target.checked })}
                  className="w-4 h-4 text-spruce-600 rounded focus:ring-spruce-500"
                />
                <label htmlFor="preferEmail" className="ml-2 text-sm text-gray-700">
                  Prefer Email Reminders
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                rows="3"
                placeholder="Additional notes..."
              />
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-spruce-600 text-white rounded-lg hover:bg-spruce-700 font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  submitText
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const PatientsPage = ({ config, currentUser }) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions(currentUser);

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [errorModal, setErrorModal] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    cellphoneProvider: '',
    dateOfBirth: '',
    notes: '',
    preferSMS: false,
    preferEmail: true
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPatients, setTotalPatients] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const patientsPerPage = 25;

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
    } else {
      setErrorModal({ title: 'Invalid File', message: 'Please select a valid CSV file.' });
    }
  };

  const downloadTemplate = () => {
    const csvContent = `name,dateOfBirth,email,phone,notes
John Doe,15-03-1985,john.doe@example.com,555-1234,Patient has diabetes
Jane Smith,22-07-1990,jane.smith@example.com,555-5678,Allergic to penicillin
Robert Johnson,30-11-1978,robert.j@example.com,555-9012,No known allergies`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'patient-import-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!csvFile) {
      setErrorModal({ title: 'No File Selected', message: 'Please select a CSV file first.' });
      return;
    }

    try {
      setImporting(true);
      setImportResults(null);

      // Read file content
      const fileContent = await csvFile.text();

      // Send to API
      const result = await ApiService.bulkImportPatients(fileContent);

      setImportResults(result);

      // Reload patients list after successful import
      if (result.summary.successful > 0) {
        loadPatients();
      }
    } catch (err) {
      console.error('Import error:', err);
      setErrorModal({ title: 'Import Failed', message: 'Failed to import patients: ' + (err.message || 'Unknown error') });
    } finally {
      setImporting(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setCsvFile(null);
    setImportResults(null);
  };

  const t = config?.terminology || {
    client: { singular: 'Patient', plural: 'Patients', singularLower: 'patient', pluralLower: 'patients' }
  };

  useEffect(() => {
    loadPatients();
  }, []);

  // Reload when search term changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1); // Reset to page 1 when searching
      loadPatients(1);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadPatients = async (page = 1) => {
    try {
      setLoading(true);

      // Build query params
      const params = new URLSearchParams();
      params.append('page', page.toString());
      
      // Add search term if present
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      const response = await ApiService.getPatients(`?${params.toString()}`);

      setPatients(response.patients || []);
      setCurrentPage(page);
      setHasMore(response.hasMore || false); // Track if there are more pages
      
      // Update total from response
      if (response.totalResults !== undefined) {
        setTotalPatients(response.totalResults);
      }

    } catch (err) {
      console.error('Failed to load patients:', err);
    } finally {
      setLoading(false);
    }
  };

  const goToPage = (page) => {
    loadPatients(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      cellphoneProvider: '',
      dateOfBirth: '',
      notes: '',
      preferSMS: false,
      preferEmail: true
    });
    setError('');
    setSelectedPatient(null);
  };

  const openEditModal = (patient) => {
    const nameParts = patient.name ? patient.name.split(' ') : ['', ''];
    const firstName = patient.firstName || nameParts[0] || '';
    const lastName = patient.lastName || nameParts.slice(1).join(' ') || '';

    setSelectedPatient(patient);
    setFormData({
      firstName,
      lastName,
      email: patient.email,
      phone: patient.phone || '',
      cellphoneProvider: patient.cellphoneProvider || '',
      dateOfBirth: patient.dateOfBirth || '',
      notes: patient.notes || '',
      preferSMS: patient.preferSMS || false,
      preferEmail: patient.preferEmail !== undefined ? patient.preferEmail : true
    });
    setShowEditModal(true);
  };

  const handleAddPatient = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const patientData = {
        ...formData,
        name: `${formData.firstName} ${formData.lastName}`.trim()
      };

      const newPatient = await ApiService.createPatient(patientData);

      // Reload the entire patient list to ensure consistency
      await loadPatients();

      setShowAddModal(false);
      resetForm();
    } catch (err) {
      setError('Failed to create patient');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleEditPatient = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const patientData = {
        ...formData,
        name: `${formData.firstName} ${formData.lastName}`.trim()
      };

      await ApiService.updatePatient(selectedPatient.patientId, patientData);
      setPatients(patients.map(p =>
        p.patientId === selectedPatient.patientId
          ? { ...p, ...patientData }
          : p
      ));
      setShowEditModal(false);
      resetForm();
    } catch (err) {
      setError('Failed to update patient');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePatient = (patient) => {
    setPatientToDelete(patient);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await ApiService.deletePatient(patientToDelete.patientId);
      setPatients(patients.filter(p => p.patientId !== patientToDelete.patientId));
      setShowDeleteConfirm(false);
      setPatientToDelete(null);
    } catch (err) {
      console.error('Failed to delete patient:', err);
      setErrorModal({ title: 'Error Deleting Patient', message: err.message || 'Failed to delete patient. Please try again.' });
    } finally {
      setDeleting(false);
    }
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${month}-${day}-${year}`;
  };

  // No client-side filtering or sorting needed - done server-side
  const filteredPatients = patients;

  // Check if user has permission to view patients
  if (!hasPermission(PERMISSIONS.PATIENTS_VIEW)) {
    return <AccessDenied resource="patients" action="view" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t.client.plural}</h1>
          <p className="text-gray-600 mt-1">Manage your {t.client.pluralLower}</p>
        </div>
        <div className="flex gap-2">
          {hasPermission(PERMISSIONS.PATIENTS_ADD) && (
            <>
              <button
                onClick={() => setShowImportModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Upload size={20} />
                Import Patients
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-spruce-600 text-white rounded-lg hover:bg-spruce-700 transition-colors"
              >
                <Plus size={20} />
                Add {t.client.singular}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Patient Stats Dashboard */}
      <PatientStatsWidget 
        config={config} 
        patients={filteredPatients}
        onRefresh={loadPatients}
      />

      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder={`Search ${t.client.pluralLower}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <Loader className="w-8 h-8 text-spruce-600 animate-spin mx-auto" />
          </div>
        ) : filteredPatients.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {searchTerm ? `No ${t.client.pluralLower} found matching "${searchTerm}"` : `No ${t.client.pluralLower} yet`}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date of Birth</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredPatients.map((patient) => (
                  <tr key={patient.patientId} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{patient.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">{patient.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">{patient.phone || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                      {patient.dateOfBirth ? formatDateForDisplay(patient.dateOfBirth) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex gap-2 justify-end">
                        {hasPermission(PERMISSIONS.PATIENTS_EDIT) && (
                          <button
                            onClick={() => openEditModal(patient)}
                            className="p-2 text-spruce-600 hover:bg-spruce-50 rounded transition-colors"
                            title="Edit patient"
                          >
                            <Edit size={18} />
                          </button>
                        )}
                        {hasPermission(PERMISSIONS.PATIENTS_DELETE) && (
                          <button
                            onClick={() => handleDeletePatient(patient)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Delete patient"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                        {hasPermission(PERMISSIONS.PATIENTS_VIEW) && (
                          <button
                            onClick={() => navigate(`/patients/${patient.patientId}`)}
                            className="p-2 text-spruce-600 hover:bg-spruce-50 rounded transition-colors"
                            title="View patient details"
                          >
                            <User size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && filteredPatients.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-4 mt-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {((currentPage - 1) * patientsPerPage) + 1} to {Math.min(currentPage * patientsPerPage, ((currentPage - 1) * patientsPerPage) + filteredPatients.length)} {searchTerm && 'of filtered results'}
            </div>

            <div className="flex items-center gap-2">
              {/* Previous Button */}
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                title="Previous page"
              >
                <ChevronLeft size={20} />
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {(() => {
                  const totalPages = Math.ceil(totalPatients / patientsPerPage) || currentPage;
                  const maxVisible = 7;
                  let pages = [];

                  if (totalPages <= maxVisible) {
                    // Show all pages
                    pages = Array.from({ length: totalPages }, (_, i) => i + 1);
                  } else {
                    // Show smart pagination
                    if (currentPage <= 4) {
                      // Near start: 1 2 3 4 5 ... 28
                      pages = [1, 2, 3, 4, 5, '...', totalPages];
                    } else if (currentPage >= totalPages - 3) {
                      // Near end: 1 ... 24 25 26 27 28
                      pages = [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
                    } else {
                      // Middle: 1 ... 12 13 14 ... 28
                      pages = [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
                    }
                  }

                  return pages.map((page, index) => {
                    if (page === '...') {
                      return (
                        <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-400">
                          ...
                        </span>
                      );
                    }

                    const isActive = page === currentPage;
                    return (
                      <button
                        key={page}
                        onClick={() => goToPage(page)}
                        className={`min-w-[40px] px-3 py-2 rounded-lg font-medium transition ${isActive
                            ? 'bg-spruce-600 text-white'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        {page}
                      </button>
                    );
                  });
                })()}
              </div>

              {/* Next Button */}
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={!hasMore}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                title="Next page"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          {/* Page info */}
          <div className="text-center text-sm text-gray-500 mt-3">
            {searchTerm ? (
              <>Filtered {filteredPatients.length} {t.client.pluralLower}</>
            ) : (
              <>Page {currentPage} {totalPatients > 0 && `of ${Math.ceil(totalPatients / patientsPerPage)}`}</>
            )}
          </div>
        </div>
      )}

      {/* Add Patient Modal */}
      <PatientModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          resetForm();
        }}
        onSubmit={handleAddPatient}
        title={`Add ${t.client.singular}`}
        submitText={`Add ${t.client.singular}`}
        formData={formData}
        setFormData={setFormData}
        error={error}
        saving={saving}
        t={t}
      />

      {/* Edit Patient Modal */}
      <PatientModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          resetForm();
        }}
        onSubmit={handleEditPatient}
        title={`Edit ${t.client.singular}`}
        submitText="Save Changes"
        formData={formData}
        setFormData={setFormData}
        error={error}
        saving={saving}
        t={t}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && patientToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 mx-4">
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-red-100 p-3 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Delete {t.client.singular}?</h3>
                <p className="text-gray-600">
                  Are you sure you want to delete <span className="font-semibold">{patientToDelete.name}</span>?
                </p>
                <div className="mt-4 p-3 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-800 font-semibold mb-2">This will permanently delete:</p>
                  <ul className="text-sm text-red-700 space-y-1">
                    <li>• {t.client.singular} information</li>
                    <li>• All protocols</li>
                    <li>• All medications</li>
                    <li>• All services</li>
                  </ul>
                </div>
                <p className="text-sm text-gray-600 mt-4">This action cannot be undone.</p>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete {t.client.singular}
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setPatientToDelete(null);
                }}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ADD THIS ENTIRE MODAL HERE - before the ); */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Upload size={24} />
                Import Patients from CSV
              </h2>
              <button
                onClick={closeImportModal}
                className="text-gray-500 hover:text-gray-700"
                disabled={importing}
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Instructions */}
              <div className="bg-spruce-50 border border-spruce-200 rounded-lg p-4">
                <h3 className="font-semibold text-spruce-900 mb-2 flex items-center gap-2">
                  <FileText size={18} />
                  How to use:
                </h3>
                <ol className="text-sm text-spruce-800 space-y-1 list-decimal list-inside">
                  <li>Download the CSV template below</li>
                  <li>Fill in your patient data following the format</li>
                  <li>Upload the completed CSV file</li>
                  <li>Review the import results</li>
                </ol>
              </div>

              {/* CSV Format Requirements */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Required CSV Format:</h3>
                <code className="text-xs bg-gray-800 text-green-400 p-2 rounded block overflow-x-auto">
                  name,dateOfBirth,email,phone,notes
                </code>
                <p className="text-xs text-gray-600 mt-2">
                  <strong>Required:</strong> name (first and last name separated by space)<br />
                  <strong>Optional:</strong> dateOfBirth (DD-MM-YYYY), email, phone, notes
                </p>
              </div>

              {/* Download Template Button */}
              <button
                onClick={downloadTemplate}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg flex items-center justify-center gap-2"
              >
                <Download size={18} />
                Download CSV Template
              </button>

              {/* File Upload */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="csv-upload"
                  disabled={importing}
                />
                <label
                  htmlFor="csv-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload size={48} className="text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">
                    {csvFile ? csvFile.name : 'Click to select CSV file'}
                  </span>
                </label>
              </div>

              {/* Import Results */}
              {importResults && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Import Results</h3>

                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-spruce-50 p-3 rounded">
                      <div className="text-2xl font-bold text-spruce-900">
                        {importResults.summary.total}
                      </div>
                      <div className="text-xs text-spruce-600">Total</div>
                    </div>
                    <div className="bg-green-50 p-3 rounded">
                      <div className="text-2xl font-bold text-green-900">
                        {importResults.summary.successful}
                      </div>
                      <div className="text-xs text-green-600">Successful</div>
                    </div>
                    <div className="bg-red-50 p-3 rounded">
                      <div className="text-2xl font-bold text-red-900">
                        {importResults.summary.failed}
                      </div>
                      <div className="text-xs text-red-600">Failed</div>
                    </div>
                  </div>

                  {/* Successful Imports */}
                  {importResults.results.success.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-medium text-green-900 mb-2 flex items-center gap-2">
                        <CheckCircle size={16} />
                        Successfully Imported ({importResults.results.success.length})
                      </h4>
                      <div className="max-h-40 overflow-y-auto bg-green-50 rounded p-2">
                        {importResults.results.success.map((item, idx) => (
                          <div key={idx} className="text-xs text-green-800 py-1">
                            Line {item.line}: {item.name} - {item.email}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Failed Imports */}
                  {importResults.results.failed.length > 0 && (
                    <div>
                      <h4 className="font-medium text-red-900 mb-2 flex items-center gap-2">
                        <XCircle size={16} />
                        Failed Imports ({importResults.results.failed.length})
                      </h4>
                      <div className="max-h-40 overflow-y-auto bg-red-50 rounded p-2">
                        {importResults.results.failed.map((item, idx) => (
                          <div key={idx} className="text-xs text-red-800 py-1">
                            Line {item.line}: {item.name} - {item.error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Import Button */}
              <div className="flex gap-2 pt-4">
                <button
                  onClick={handleImport}
                  disabled={!csvFile || importing}
                  className="flex-1 bg-spruce-600 text-white py-2 px-4 rounded-lg hover:bg-spruce-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {importing ? (
                    <>
                      <Loader className="animate-spin" size={18} />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      Import Patients
                    </>
                  )}
                </button>
                <button
                  onClick={closeImportModal}
                  disabled={importing}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                >
                  {importResults ? 'Close' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Error Modal */}
      {errorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-800">{errorModal.title}</h3>
            </div>
            <p className="text-gray-600 mb-6">
              {errorModal.message}
            </p>
            <button
              onClick={() => setErrorModal(null)}
              className="w-full bg-spruce-600 text-white py-2 px-4 rounded-lg hover:bg-spruce-700 transition"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientsPage;